import {
  createAdminDailyDealsCompanyApi,
  createAdminDailyDealsDiscountApi,
  deleteAdminDailyDealsCompanyApi,
  deleteAdminDailyDealsDiscountApi,
  fetchAdminDailyDealsCompaniesApi,
  fetchAdminDailyDealsDiscountsApi,
  updateAdminDailyDealsCompanyApi,
  updateAdminDailyDealsDiscountApi,
} from '../../util/api';

const ns = 'app/AdminPanelDailyDealsPage';

export const FETCH_DAILY_DEALS_REQUEST = `${ns}/FETCH_DAILY_DEALS_REQUEST`;
export const FETCH_DAILY_DEALS_SUCCESS = `${ns}/FETCH_DAILY_DEALS_SUCCESS`;
export const FETCH_DAILY_DEALS_ERROR = `${ns}/FETCH_DAILY_DEALS_ERROR`;
export const MUTATE_DAILY_DEALS_REQUEST = `${ns}/MUTATE_DAILY_DEALS_REQUEST`;
export const MUTATE_DAILY_DEALS_SUCCESS = `${ns}/MUTATE_DAILY_DEALS_SUCCESS`;
export const MUTATE_DAILY_DEALS_ERROR = `${ns}/MUTATE_DAILY_DEALS_ERROR`;

const getErrorMessage = error =>
  error?.message || error?.error || 'Something went wrong. Please try again.';

const initialState = {
  companies: [],
  discounts: [],
  companyPagination: null,
  discountPagination: null,
  fetchInProgress: false,
  fetchError: null,
  mutationInProgress: false,
  mutationError: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case FETCH_DAILY_DEALS_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_DAILY_DEALS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        companies: action.payload.companies || [],
        discounts: action.payload.discounts || [],
        companyPagination: action.payload.companyPagination || null,
        discountPagination: action.payload.discountPagination || null,
      };
    case FETCH_DAILY_DEALS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: action.payload };
    case MUTATE_DAILY_DEALS_REQUEST:
      return { ...state, mutationInProgress: true, mutationError: null };
    case MUTATE_DAILY_DEALS_SUCCESS:
      return { ...state, mutationInProgress: false };
    case MUTATE_DAILY_DEALS_ERROR:
      return { ...state, mutationInProgress: false, mutationError: action.payload };
    default:
      return state;
  }
}

export const fetchDailyDealsAdminData = params => async dispatch => {
  dispatch({ type: FETCH_DAILY_DEALS_REQUEST });
  try {
    const companyParams = params?.companies || {};
    const discountParams = params?.discounts || {};
    const [companiesResponse, discountsResponse] = await Promise.all([
      fetchAdminDailyDealsCompaniesApi(companyParams),
      fetchAdminDailyDealsDiscountsApi(discountParams),
    ]);

    const payload = {
      companies: companiesResponse?.data || [],
      discounts: discountsResponse?.data || [],
      companyPagination: companiesResponse?.pagination || null,
      discountPagination: discountsResponse?.pagination || null,
    };

    dispatch({ type: FETCH_DAILY_DEALS_SUCCESS, payload });
    return payload;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: FETCH_DAILY_DEALS_ERROR, payload: message });
    throw error;
  }
};

const mutateAndRefresh = (request, params) => async dispatch => {
  dispatch({ type: MUTATE_DAILY_DEALS_REQUEST });
  try {
    const response = await request();
    dispatch({ type: MUTATE_DAILY_DEALS_SUCCESS });
    await dispatch(fetchDailyDealsAdminData(params));
    return response;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: MUTATE_DAILY_DEALS_ERROR, payload: message });
    throw error;
  }
};

export const createDailyDealsCompany = (payload, params) =>
  mutateAndRefresh(() => createAdminDailyDealsCompanyApi(payload), params);

export const bulkCreateDailyDealsCompanies = (companies, params) => async dispatch => {
  dispatch({ type: MUTATE_DAILY_DEALS_REQUEST });
  try {
    const responses = [];
    for (const company of companies) {
      responses.push(await createAdminDailyDealsCompanyApi(company));
    }
    dispatch({ type: MUTATE_DAILY_DEALS_SUCCESS });
    await dispatch(fetchDailyDealsAdminData(params));
    return responses;
  } catch (error) {
    const message = getErrorMessage(error);
    dispatch({ type: MUTATE_DAILY_DEALS_ERROR, payload: message });
    throw error;
  }
};

export const updateDailyDealsCompany = (companyId, payload, params) =>
  mutateAndRefresh(() => updateAdminDailyDealsCompanyApi(companyId, payload), params);

export const deleteDailyDealsCompany = (companyId, params) =>
  mutateAndRefresh(() => deleteAdminDailyDealsCompanyApi(companyId), params);

export const createDailyDealsDiscount = (payload, params) =>
  mutateAndRefresh(() => createAdminDailyDealsDiscountApi(payload), params);

export const updateDailyDealsDiscount = (discountId, payload, params) =>
  mutateAndRefresh(() => updateAdminDailyDealsDiscountApi(discountId, payload), params);

export const deleteDailyDealsDiscount = (discountId, params) =>
  mutateAndRefresh(() => deleteAdminDailyDealsDiscountApi(discountId), params);

export const loadData = () => () => Promise.resolve();
